import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Collapse, Divider, Tooltip, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { colors, typography } from '../../styles/theme';
import { getRecordLeadViaLabel } from '../recordLeads';
import { groupInquiriesByRecord } from '../inquiryLeadGroups';
import { LEADS_TEXT_MUTED, LEADS_TEXT_PRIMARY } from './leadsUi.styles';
import LeadTemperatureDot from './LeadTemperatureDot';
import LeadInterestStageMenu from './LeadInterestStageMenu';
import { InquiriesCollapse } from './LeadItemChips';
import LeadInquiryCard from './LeadInquiryCard';

function formatInquiryCount(n) {
  const count = Math.max(0, Number(n) || 0);
  return count === 1 ? '1 consulta' : `${count} consultas`;
}

/** Ancho fijo de nombre + puntito: alinea divider/etapa entre filas. */
const LEAD_NAME_COL_WIDTH = 120;

const metaTextSx = {
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: 1.2,
  color: colors.textSecondary,
  fontFamily: typography.fontFamily.primary,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const headerDividerSx = {
  mx: 1,
  height: 14,
  alignSelf: 'center',
  borderColor: 'rgba(255,255,255,0.16)',
};

/** Sub-card por registro (mismo look que Intereses y consultas / LeadItemChips). */
const recordCardSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  px: 1.5,
  py: 1.25,
  borderRadius: '8px',
  bgcolor: 'rgba(255,255,255,0.04)',
};

/** `flatInquiries`: sin recuadro contenedor; solo las tarjetas de consulta. */
const flatRecordSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
};

const kindSx = {
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: LEADS_TEXT_MUTED,
  lineHeight: 1.2,
};

/**
 * Grupo lead → consultas anidadas (bandeja y “Leads y consultas”).
 * Colapsable (cerrado por defecto). Header en una sola fila:
 * [Nombre…·temp 120px] | [etapa|via] …… [N consultas ▸].
 * Sin chip “Lead”.
 *
 * @param {{
 *   group: {
 *     leadId: string,
 *     leadName: string,
 *     temperature?: string|null,
 *     via?: string,
 *     interestStage?: string|null,
 *     offerAmount?: number|null,
 *     offerCurrency?: string|null,
 *     discardReason?: string|null,
 *     unseenInquiryCount?: number,
 *     inquiries: object[],
 *   },
 *   expandedIds: Set<string>,
 *   onToggleInquiry?: (row: object) => void,
 *   onMarkSeen?: (row: object) => void,
 *   onOpenLead?: (leadId: string) => void,
 *   onOpenItem?: ((target: object) => void)|null,
 *   showVia?: boolean,
 *   hideItemLabel?: boolean,
 *   defaultExpanded?: boolean,
 *   flatInquiries?: boolean,
 * }} props
 *
 * `flatInquiries` («Leads y consultas» de las fichas): sin el collapse intermedio
 * «N consultas · última …»; al abrir el lead se ven las consultas (cada una con su
 * fecha) y se marcan vistas. Sin él (CollectionLeadsDialog), igual que antes.
 */
export default function InquiryLeadGroup({
  group,
  expandedIds,
  onToggleInquiry,
  onMarkSeen = null,
  onOpenLead,
  onOpenItem = null,
  showVia = false,
  hideItemLabel = false,
  defaultExpanded = false,
  flatInquiries = false,
}) {
  const [open, setOpen] = useState(defaultExpanded);
  const inquiryCount = group.inquiries.length;
  const canCollapse = inquiryCount > 0;
  const interestStage = showVia ? (group.interestStage || null) : null;
  const via = showVia && !interestStage ? getRecordLeadViaLabel(group.via) : '';
  const showMeta = Boolean(interestStage || via);
  const countLabel = formatInquiryCount(inquiryCount);
  const recordGroups = useMemo(
    () => groupInquiriesByRecord(group.inquiries),
    [group.inquiries],
  );
  // expandedIds / onOpenItem: API estable (huérfanos en el dialog usan InquiryCard).
  void expandedIds;
  void onOpenItem;
  const markRowsSeen = (rows) => {
    const mark = onMarkSeen || onToggleInquiry;
    if (!mark) return;
    (Array.isArray(rows) ? rows : []).forEach((row) => mark(row));
  };

  return (
    <Box
      data-testid="inquiry-lead-group"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        px: 1.25,
        py: 1.25,
        borderRadius: '8px',
        bgcolor: colors.backgroundDark,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 0.75,
          minWidth: 0,
          width: '100%',
        }}
      >
        <ButtonBase
          onClick={() => onOpenLead?.(group.leadId)}
          disabled={!onOpenLead}
          aria-label={`Abrir lead ${group.leadName || ''}`.trim()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            flex: '1 1 auto',
            minWidth: 0,
            maxWidth: '100%',
            textAlign: 'left',
            borderRadius: '6px',
            py: 0.25,
            px: 0.25,
            '&.MuiButtonBase-root': { justifyContent: 'flex-start' },
            '&:hover': onOpenLead ? { opacity: 0.92 } : undefined,
          }}
        >
          <Box
            sx={{
              width: LEAD_NAME_COL_WIDTH,
              minWidth: 0,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Tooltip title={group.leadName || 'Sin nombre'} enterDelay={400} placement="top">
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textAlign: 'left' }}>
                <Typography
                  noWrap
                  component="span"
                  sx={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                  }}
                >
                  {group.leadName || 'Sin nombre'}
                </Typography>
              </Box>
            </Tooltip>
            <LeadTemperatureDot
              temperature={group.temperature}
              testId="inquiry-lead-temperature"
            />
          </Box>
          {showMeta ? (
            <>
              <Divider orientation="vertical" flexItem sx={headerDividerSx} />
              {interestStage ? (
                <LeadInterestStageMenu
                  item={{
                    stage: interestStage,
                    offer_amount: group.offerAmount,
                    offer_currency: group.offerCurrency,
                    discard_reason: group.discardReason,
                  }}
                  editable={false}
                  muted
                />
              ) : (
                <Typography component="span" sx={metaTextSx}>
                  {via}
                </Typography>
              )}
            </>
          ) : null}
        </ButtonBase>

        <Box sx={{ flex: '1 1 auto', minWidth: 8 }} aria-hidden />

        {group.unseenInquiryCount > 0 ? (
          <Box
            aria-label="Consultas nuevas"
            title={`${group.unseenInquiryCount} sin leer`}
            sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.primary, flexShrink: 0 }}
          />
        ) : null}

        {canCollapse ? (
          <ButtonBase
            aria-expanded={open}
            aria-label={open ? `Ocultar ${countLabel}` : `Ver ${countLabel}`}
            onClick={() => setOpen((v) => {
              const next = !v;
              if (next && flatInquiries) markRowsSeen(group.inquiries);
              return next;
            })}
            data-testid="inquiry-lead-group-toggle"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              alignSelf: 'center',
              gap: 0.25,
              flexShrink: 0,
              borderRadius: '6px',
              px: 0.5,
              py: 0.25,
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: 1.2,
              color: colors.textSecondary,
              fontFamily: typography.fontFamily.primary,
              '& .MuiSvgIcon-root': {
                fontSize: 16,
                color: 'rgba(255,255,255,0.45)',
                transition: 'transform 150ms',
                transform: open ? 'rotate(90deg)' : 'none',
              },
              '&:hover': { color: colors.textPrimary },
            }}
          >
            <Typography component="span" sx={{ ...metaTextSx, color: 'inherit' }}>
              {countLabel}
            </Typography>
            <ChevronRightIcon />
          </ButtonBase>
        ) : showVia && showMeta ? null : (
          <Typography sx={{ ...metaTextSx, alignSelf: 'center' }}>
            Sin consultas
          </Typography>
        )}
      </Box>

      {canCollapse ? (
        <Collapse in={open} unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pt: 0.25 }}>
            {recordGroups.map((rec) => (
              <Box
                key={rec.key}
                data-testid="inquiry-record-group"
                data-flat={flatInquiries ? 'true' : undefined}
                sx={flatInquiries ? flatRecordSx : recordCardSx}
              >
                {!hideItemLabel ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={kindSx}>{rec.kindLabel}</Typography>
                      <Typography
                        sx={{
                          mt: 0.25,
                          fontSize: '13px',
                          fontWeight: 600,
                          lineHeight: 1.3,
                          color: LEADS_TEXT_PRIMARY,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={rec.title}
                      >
                        {rec.title}
                      </Typography>
                    </Box>
                    {rec.interestStage ? (
                      <LeadInterestStageMenu
                        item={{
                          stage: rec.interestStage,
                          offer_amount: rec.offerAmount,
                          offer_currency: rec.offerCurrency,
                          discard_reason: rec.discardReason,
                        }}
                        editable={false}
                        muted
                      />
                    ) : null}
                  </Box>
                ) : (
                  rec.interestStage ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                      <LeadInterestStageMenu
                        item={{
                          stage: rec.interestStage,
                          offer_amount: rec.offerAmount,
                          offer_currency: rec.offerCurrency,
                          discard_reason: rec.discardReason,
                        }}
                        editable={false}
                        muted
                      />
                    </Box>
                  ) : null
                )}
                <Box
                  sx={flatInquiries
                    ? {}
                    : { mt: hideItemLabel && !rec.interestStage ? 0 : 1, pt: hideItemLabel && !rec.interestStage ? 0 : 0.75, borderTop: hideItemLabel && !rec.interestStage ? 'none' : '1px solid rgba(255,255,255,0.06)' }}
                >
                  {flatInquiries ? (
                    <Box data-testid="inquiry-record-inquiries" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {rec.inquiries.map((inq, index) => (
                        <LeadInquiryCard key={inq.id || `${inq.created_at}-${index}`} inquiry={inq} compact />
                      ))}
                    </Box>
                  ) : (
                    <InquiriesCollapse
                      inquiries={rec.inquiries}
                      total={rec.inquiries.length}
                      compact
                      testId="inquiry-record-inquiries"
                      onExpand={markRowsSeen}
                    />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Collapse>
      ) : null}
    </Box>
  );
}
